<template>
    <BaseForm>
        <template #fields>
            <TextAreaField
                :value="values.notesAppointmentRules"
                :label="$t('notes-appointment-rules')"
                :error-messages="errors.notesAppointmentRules"
                :max-length="EventSeriesNotesAppointmentRules.shape.notesAppointmentRules.maxLength ?? undefined"
                @input="setFieldValue('notesAppointmentRules', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesAppointmentRules', true)"
            />
            <TextAreaField
                :value="values.notesMonths"
                :label="$t('notes-months')"
                :error-messages="errors.notesMonths"
                :max-length="EventSeriesNotesAppointmentRules.shape.notesMonths.maxLength ?? undefined"
                @input="setFieldValue('notesMonths', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesMonths', true)"
            />
            <TextAreaField
                :value="values.notesHolidaysNrw"
                :label="$t('notes-holidays-nrw')"
                :error-messages="errors.notesHolidaysNrw"
                :max-length="EventSeriesNotesAppointmentRules.shape.notesHolidaysNrw.maxLength ?? undefined"
                @input="setFieldValue('notesHolidaysNrw', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesHolidaysNrw', true)"
            />
            <TextAreaField
                :value="values.notesHolidaysNational"
                :label="$t('notes-holidays-national')"
                :error-messages="errors.notesHolidaysNational"
                :max-length="EventSeriesNotesAppointmentRules.shape.notesHolidaysNational.maxLength ?? undefined"
                @input="setFieldValue('notesHolidaysNational', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesHolidaysNational', true)"
            />
            <TextAreaField
                :value="values.notesHolidaysInternational"
                :label="$t('notes-holidays-international')"
                :error-messages="errors.notesHolidaysInternational"
                :max-length="EventSeriesNotesAppointmentRules.shape.notesHolidaysInternational.maxLength ?? undefined"
                @input="setFieldValue('notesHolidaysInternational', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesHolidaysInternational', true)"
            />
            <TextAreaField
                :value="values.notesPublicHolidaysNrw"
                :label="$t('notes-public-holidays-nrw')"
                :error-messages="errors.notesPublicHolidaysNrw"
                :max-length="EventSeriesNotesAppointmentRules.shape.notesPublicHolidaysNrw.maxLength ?? undefined"
                @input="setFieldValue('notesPublicHolidaysNrw', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesPublicHolidaysNrw', true)"
            />
            <TextAreaField
                :value="values.notesPublicHolidaysNational"
                :label="$t('notes-public-holidays-national')"
                :error-messages="errors.notesPublicHolidaysNational"
                :max-length="EventSeriesNotesAppointmentRules.shape.notesPublicHolidaysNational.maxLength ?? undefined"
                @input="setFieldValue('notesPublicHolidaysNational', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesPublicHolidaysNational', true)"
            />
            <TextAreaField
                :value="values.notesPublicHolidaysInternational"
                :label="$t('notes-public-holidays-international')"
                :error-messages="errors.notesPublicHolidaysInternational"
                :max-length="
                    EventSeriesNotesAppointmentRules.shape.notesPublicHolidaysInternational.maxLength ?? undefined
                "
                @input="setFieldValue('notesPublicHolidaysInternational', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesPublicHolidaysInternational', true)"
            />
            <TextAreaField
                :value="values.otherNotesAppointmentRules"
                :label="$t('other-notes-appointment-rules')"
                :error-messages="errors.otherNotesAppointmentRules"
                :max-length="EventSeriesNotesAppointmentRules.shape.otherNotesAppointmentRules.maxLength ?? undefined"
                @input="setFieldValue('otherNotesAppointmentRules', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('otherNotesAppointmentRules', true)"
            />
            <TextAreaField
                :value="values.appointmentHistory"
                :label="$t('appointment-history')"
                :error-messages="errors.appointmentHistory"
                :max-length="EventSeriesNotesAppointmentRules.shape.appointmentHistory.maxLength ?? undefined"
                @input="setFieldValue('appointmentHistory', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('appointmentHistory', true)"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { EventSeriesNotesAppointmentRules } from '../schemas';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);
const { series, updateEventSeries } = useEventSeriesInjection();

const { errors, values, setFieldValue, setFieldTouched, validate, onDiscard, resetForm } = useForm(
    EventSeriesNotesAppointmentRules,
    computed(() => ({
        notesAppointmentRules: series.value?.notesAppointmentRules ?? '',
        notesMonths: series.value?.notesMonths ?? '',
        notesHolidaysNrw: series.value?.notesHolidaysNrw ?? '',
        notesHolidaysNational: series.value?.notesHolidaysNational ?? '',
        notesHolidaysInternational: series.value?.notesHolidaysInternational ?? '',
        notesPublicHolidaysNrw: series.value?.notesPublicHolidaysNrw ?? '',
        notesPublicHolidaysNational: series.value?.notesPublicHolidaysNational ?? '',
        notesPublicHolidaysInternational: series.value?.notesPublicHolidaysInternational ?? '',
        otherNotesAppointmentRules: series.value?.otherNotesAppointmentRules ?? '',
        appointmentHistory: series.value?.appointmentHistory ?? '',
    })),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await updateEventSeries({ id: seriesId.value, ...values.value });
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
